import React, { useCallback, useState } from 'react';
import { cssMap } from '@compiled/react';

import { Stack } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import {
	SpecificAccessDetails,
	type SpecificAccessDetailsProps,
} from '../specificAccessDetails/SpecificAccessDetails.tsx';

import { SpecificAccessFilter } from './SpecificAccessFilter.tsx';
import { RemoveAllSpecificAccessConfirmDialog } from './RemoveAllSpecificAccessConfirmDialog.tsx';
import { useSpecificAccessFilter } from './specificAccessDialogHooks.ts';

export type SpecificAccessPeopleTabProps = {
	principalCounts: {
		countOfIndividuals: number;
		countOfTeams: number;
		countOfGroups: number;
	};
	specificAccessDetails?: SpecificAccessDetailsProps;
	onRemoveAll: () => Promise<void>;
};

const styles = cssMap({
	specificAccessPeopleTabStyle: { marginTop: token('space.200') },
});

export const SpecificAccessPeopleTab = ({
	principalCounts,
	specificAccessDetails,
	onRemoveAll,
}: SpecificAccessPeopleTabProps) => {
	const [isRemoveAllConfirmOpen, setIsRemoveAllConfirmOpen] = useState(false);
	const [isRemoveAllConfirmDisabled, setIsRemoveAllConfirmDisabled] = useState(false);

	const openRemoveAllConfirm = useCallback(() => setIsRemoveAllConfirmOpen(true), []);
	const closeRemoveAllConfirm = useCallback(() => {
		setIsRemoveAllConfirmOpen(false);
		setIsRemoveAllConfirmDisabled(false);
	}, []);

	const confirmRemoveAll = useCallback(async () => {
		setIsRemoveAllConfirmDisabled(true);
		try {
			await onRemoveAll();
			setIsRemoveAllConfirmOpen(false);
		} finally {
			// TODO(share-and-restrict-dialog): If mutation fails later, we can surface a flag and re-enable.
			setIsRemoveAllConfirmDisabled(false);
		}
	}, [onRemoveAll]);
	const {
		filterText,
		setFilterText,
		selectedAccessType,
		setSelectedAccessType,
		filteredSpecificAccessDetails,
	} = useSpecificAccessFilter({ specificAccessDetails });

	return (
		<Stack xcss={styles.specificAccessPeopleTabStyle}>
			<SpecificAccessFilter
				setFilterText={setFilterText}
				filterText={filterText}
				countOfGroups={principalCounts.countOfGroups}
				countOfTeams={principalCounts.countOfTeams}
				countOfIndividuals={principalCounts.countOfIndividuals}
				selectedAccessType={selectedAccessType}
				onAccessTypeChange={setSelectedAccessType}
				onOpenRemoveAllConfirm={openRemoveAllConfirm}
			/>

			<RemoveAllSpecificAccessConfirmDialog
				isOpen={isRemoveAllConfirmOpen}
				isConfirmDisabled={isRemoveAllConfirmDisabled}
				onCancel={closeRemoveAllConfirm}
				onConfirm={confirmRemoveAll}
			/>
			{filteredSpecificAccessDetails && (
				<SpecificAccessDetails {...filteredSpecificAccessDetails} />
			)}
		</Stack>
	);
};
